import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfileForm, PROFILE_FIELDS, CONTACT_FIELDS, TAX_FIELDS } from "@/components/business/ProfileForm";
import { LogoCard, TypesCard, TimezoneCard } from "@/components/business/ProfileExtras";
import { HoursTab } from "@/components/business/HoursTab";

const Card = ({ title, children, id }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid={id}>
    {title && <h3 className="mb-4 font-display text-base font-semibold text-slate-900">{title}</h3>}{children}
  </section>
);

function BookingTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["business-hours"], queryFn: async () => (await api.get("/business/hours")).data });
  const save = useMutation({ mutationFn: (v) => api.put("/business/hours", { enforce: v }), onSuccess: (r) => { qc.setQueryData(["business-hours"], r.data); toast.success("Saved"); }, onError: (e) => toast.error(apiErr(e)) });
  if (!data) return null;
  return (
    <Card id="bs-booking" title="Booking & availability">
      <label className="flex items-start gap-3">
        <Switch checked={data.enforce} disabled={!data.can_edit || save.isPending} onCheckedChange={(v) => save.mutate(v)} data-testid="bs-enforce-hours" />
        <span><span className="block text-sm font-medium text-slate-800">Enforce operating hours for new bookings</span>
          <span className="block text-xs text-slate-500">Off: staff get a warning when booking outside hours. On: such bookings and reschedules are blocked unless an owner/admin enters a reason (recorded in the audit log). Existing bookings are never changed.</span></span>
      </label>
      <ul className="mt-4 list-disc space-y-1 pl-5 text-xs text-slate-500">
        <li>Business hours are separate from staff availability, rooms/kennels and service requirements — all existing checks still apply.</li>
        <li>Backdated corrections are not checked against hours. Multi-day stays check drop-off and pick-up times.</li>
        <li>Per-service schedules are not supported yet; services follow the business or branch hours.</li>
      </ul>
      <p className="mt-3 text-xs"><Link to="/settings" className="text-emerald-700 underline">Staff requirements per service type</Link> live in Settings → Booking requirements.</p>
    </Card>
  );
}

export default function BusinessSettings() {
  const { data, isLoading } = useQuery({ queryKey: ["business-settings"], queryFn: async () => (await api.get("/business/settings")).data });
  const [tab, setTab] = useState("profile");
  const dirtyRef = useRef({});
  const [, force] = useState(0);
  const onDirty = useCallback((k, v) => { if (!!dirtyRef.current[k] !== v) { dirtyRef.current[k] = v; force((x) => x + 1); } }, []);
  const anyDirty = Object.values(dirtyRef.current).some(Boolean);
  useEffect(() => {
    const h = (e) => { if (anyDirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [anyDirty]);
  const changeTab = (t) => { if (dirtyRef.current[tab] && !window.confirm("You have unsaved changes on this tab. Leave without saving?")) return; setTab(t); };
  if (isLoading || !data) return <Loading />;
  return (
    <div data-testid="business-settings-page">
      <PageHeader title="Business Settings" subtitle="Your business details, address, tax, operating hours and booking rules" />
      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="mb-5 flex h-auto flex-wrap justify-start gap-1 bg-slate-100 p-1">
          {[["profile", "Business Profile"], ["contact", "Address & Contact"], ["hours", "Operating Hours"], ["booking", "Booking & Availability"], ["tax", "Tax & Registration"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`bs-tab-${v}`}>{l}{dirtyRef.current[v] && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-amber-500" />}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="profile" className="space-y-5">
          <Card title="Business profile"><ProfileForm id="profile" fields={PROFILE_FIELDS} data={data} canEdit={data.can_edit} onDirty={onDirty} /></Card>
          <Card title="Logo"><LogoCard data={data} /></Card>
          <Card title="Business types"><TypesCard data={data} /></Card>
          <Card title="Time zone"><TimezoneCard data={data} /><p className="mt-2 text-xs text-slate-400">Currency: {data.tenant.currency} (fixed)</p></Card>
        </TabsContent>
        <TabsContent value="contact"><Card title="Address & contact"><ProfileForm id="contact" fields={CONTACT_FIELDS} data={data} canEdit={data.can_edit} onDirty={onDirty} /></Card></TabsContent>
        <TabsContent value="hours"><Card><HoursTab onDirty={onDirty} /></Card></TabsContent>
        <TabsContent value="booking"><BookingTab /></TabsContent>
        <TabsContent value="tax"><Card title="Tax & registration"><ProfileForm id="tax" fields={TAX_FIELDS} data={data} canEdit={data.can_edit_tax} onDirty={onDirty} />
          <p className="mt-3 text-xs text-slate-400">Invoice numbering, terms and footer stay in <Link to="/invoice-settings" className="text-emerald-700 underline">Invoice Settings</Link>. Changes apply to new invoices only.</p></Card></TabsContent>
      </Tabs>
    </div>
  );
}
